import { Logo } from "@/components/Logo";
import { ConceptBanner } from "@/concept/ConceptBanner";
import { ROLLUP_ENTITIES, ROLLUP_OWNER_NAME, rollupTotal } from "@/concept/rollupFixture";
import { formatMoneyWhole } from "@/lib/format";
import "./MultiEntityRollup.css";

/**
 * Multi-entity roll-up — CONCEPT screen (roadmap FR-2).
 *
 * Static. Nothing on this screen fetches, submits, or persists anything —
 * see `docs/demo/tradeshow-2026-09.md` "Static booth material": a picture
 * Christy talks to, deliberately not wired into the live sync path so it
 * carries zero demo-day risk. See `ConceptBanner` for why that is
 * disclosed as a permanent part of the layout rather than a footnote.
 *
 * Reached at `/concept/rollup` — a URL nothing in the visitor journey
 * (QR code -> `/entry`) ever links to. Christy reaches it deliberately from
 * the quiet link at the bottom of the laptop `Dashboard`; see `App.tsx` for
 * why this is a third hand-rolled route rather than a router dependency.
 */
export function MultiEntityRollup() {
  const total = rollupTotal(ROLLUP_ENTITIES);

  return (
    <div className="rollup">
      <header className="rollup__header">
        <Logo size={36} onDark />
        <a className="rollup__back" href="/">
          Back to dashboard
        </a>
      </header>

      <ConceptBanner detail="this is where EasyFarm is headed, shown here as a picture Christy talks to at the booth." />

      <main className="rollup__content">
        <h1 className="rollup__heading">Multi-entity roll-up</h1>
        <p className="rollup__pitch">
          &ldquo;You run five LLCs. Nobody shows you what they add up to. We will.&rdquo;
        </p>
        <p className="rollup__lede">
          Farm customers routinely run 2 to 6 legal entities. Today, seeing what they add
          up to is a once-a-year job an accountant does by hand. Below: three of{" "}
          {ROLLUP_OWNER_NAME}'s entities and the K-1 income each one passes through,
          rolling up to a single owner total that could be kept current any day, not just
          at tax time. Synthetic figures, extending the Larson Family Farms world already
          at this booth.
        </p>

        <div
          className="rollup__table-scroll"
          role="region"
          aria-label="Entity roll-up table"
          tabIndex={0}
        >
          <table className="rollup__table">
            <caption className="rollup__table-caption">
              Three Larson Family Farms entities and the K-1 income each rolls up to{" "}
              {ROLLUP_OWNER_NAME} &mdash; concept data, not live.
            </caption>
            <thead>
              <tr>
                <th scope="col">Entity</th>
                <th scope="col">Structure</th>
                <th scope="col">K-1 line item</th>
                <th scope="col">Allocated to {ROLLUP_OWNER_NAME}</th>
              </tr>
            </thead>
            <tbody>
              {ROLLUP_ENTITIES.map((entity) => (
                <tr key={entity.id}>
                  <th scope="row">
                    {entity.name}
                    <span className="rollup__entity-desc">{entity.description}</span>
                  </th>
                  <td>
                    {entity.state} &middot; {entity.structure}
                  </td>
                  <td>{entity.k1Box}</td>
                  <td className="rollup__amount">{formatMoneyWhole(entity.allocatedAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="rollup__total-row">
                <th scope="row" colSpan={3}>
                  Total K-1 flow-through to {ROLLUP_OWNER_NAME}
                </th>
                <td className="rollup__amount rollup__amount--hero">
                  {formatMoneyWhole(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </main>
    </div>
  );
}
